import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import Icon from '@mdi/react';
import {mdiPlus} from '@mdi/js';
import { alpha } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import _ from "lodash"
import {mdiInformationOutline} from '@mdi/js';
import Snackbar from '../CommonComponents/Snackbar';
import Card from '../CommonComponents/Card';
import {DialogFileSupport, DialogDelete} from '../CommonComponents/DialogBox';
import {MAX_OUTPUT_SUPPORT_FILES, MAX_SUPPORT_FILES} from '../../ApplicationConfig/constants';
import {SUPPORT_FILE_DEFAULTS} from '../../StateObjects/requestFormDefaults';

const PREFIX = 'AddSupportingFiles';

const classes = {
  addBtn: `${PREFIX}-addBtn`,
  legendInfoIcon: `${PREFIX}-legendInfoIcon`,
  filePath: `${PREFIX}-filePath`,
  filePathItem: `${PREFIX}-filePathItem`,
  errorText: `${PREFIX}-errorText`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.addBtn}`]: {
    'marginTop': theme.spacing(-1),
    'borderStyle': 'dashed',
    'width': '100%',
    'borderColor': alpha(theme.palette.common.black, 0.23),
    '&.MuiButton-outlinedPrimary:hover': {
      borderStyle: 'dashed',
    },
  },

  [`& .${classes.legendInfoIcon}`]: {
    marginTop: theme.spacing(-0.25),
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.filePath}`]: {
    display: 'flex',
    flexFlow: 'wrap',
    alignItems: 'flex-end',
  },

  [`& .${classes.filePathItem}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.errorText}`]: {
    color: theme.palette.error.main,
  }
}));

export default function AddSupportingFiles(props){

  const {t} = useTranslation();
  const {
    step,
    filesList,
    supportingFiles,
    fileType,
    readOnly,
    errors,
    setErrors,
    handleUpdateSupportingFiles,
    addBtnClass,
    mandatorySection,
    extraParagraph,
    extraNote,
  }=props;
  const errorKey = props.outputIndex !== undefined ? `outputFiles.${props.outputIndex}.` : "";
  const maxFileLimit = props.outputIndex !== undefined ? MAX_OUTPUT_SUPPORT_FILES : MAX_SUPPORT_FILES;
  const pre_name = props.outputIndex !== undefined ? `outputFile-${props.outputIndex}-` : "";

  const [alertAddFile, setAlertAddFile] = React.useState({
    open: false,
    index: null,
    newSupporting: {...SUPPORT_FILE_DEFAULTS, fileContents: step === "residualDisclosure" ? props.fileTypeDisplay : fileType}
  });
  const [dialogDeleteState, setDialogDeleteState] = React.useState({
    dialogDelete: false,
    index: null
  });
  const [snackbarFile, setSnackbarFile] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });
  const [snackbarDelete, setSnackbarDelete] = React.useState({
    open: false,
    severity: "success",
    message: ""
  })

  const handleCloseSnackbarFile=()=>{
    setSnackbarFile({...snackbarFile, open: false, message: ""});
  }

  const handleCloseSnackbarDelete=()=>{
    setSnackbarDelete({...snackbarDelete, open: false, message: ""});
  }

  const handleOpenAddFile=()=>{
    handleCloseSnackbarFile();

    setAlertAddFile({
      open: true,
      index: null,
      newSupporting: {...SUPPORT_FILE_DEFAULTS, fileContents: alertAddFile.newSupporting.fileContents}
    });
  }

  const handleOpenEditFile=(i)=>{
    handleCloseSnackbarFile();

    setAlertAddFile({
      open: true,
      index: i,
      newSupporting: {...supportingFiles[fileType][i]}
    });
  }

  const handleOpenDeleteDialog=(i)=>{
    handleCloseSnackbarDelete();
    
    setDialogDeleteState({
      dialogDelete: true,
      index: i 
    })
  }

  const handleCloseAddSupporting = () => {
    setAlertAddFile({
      ...alertAddFile,
      open: false,
      index: null,
    });
  };

  const handleAddSupportingValueChange = (name, newValue) => {
    let tempVal = {...alertAddFile.newSupporting};
    tempVal[name] = newValue;
    setAlertAddFile({...alertAddFile, newSupporting: tempVal});
  };

  const handleAddNewSupporting = () =>{
    let tempVal = {...supportingFiles};
    tempVal[fileType].push({...alertAddFile.newSupporting});
    handleUpdateSupportingFiles(tempVal);

    if(props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`]){
      let errs = _.cloneDeep(errors);
      delete errs[step][`${errorKey}supportingFiles.${fileType}`];
      setErrors(errs);
    }

    setSnackbarFile({open: true, severity: "success", message: t("requestForm.otherSupportingAdded")});
    handleCloseAddSupporting();
  };

  const handleEditNewSupporting = () =>{
    let tempVal = {...supportingFiles};
    tempVal[fileType][alertAddFile.index]={...alertAddFile.newSupporting};
    handleUpdateSupportingFiles(tempVal);
    setSnackbarFile({open: true, severity: "success", message: t("requestForm.otherSupportingEdited")});

    if(Boolean(props.errors[step]?.[`${errorKey}supportingFiles.${fileType}.${alertAddFile.index}.fileName`]) && alertAddFile.newSupporting.fileName!==""){
      let errs = _.cloneDeep(errors);
      delete errs[step][`${errorKey}supportingFiles.${fileType}.${alertAddFile.index}.fileName`];
      setErrors(errs);
    }

    handleCloseAddSupporting();
  };

  const handleCloseDeleteDialog=()=>{
    setDialogDeleteState({
      dialogDelete: false,
      index: null
    })
  }

  const handleDeleteSupportingFile=()=>{
    let tempVal = {...supportingFiles};
    tempVal[fileType].splice(dialogDeleteState.index, 1);
    handleUpdateSupportingFiles(tempVal);

    //clear errors
    let errs=_.cloneDeep(errors);
    if(errors[step]){
      Object.keys(errors[step]).forEach(k=>{
        if(k.startsWith(`${errorKey}supportingFiles.${fileType}.${dialogDeleteState.index}.`)){
          delete errs[step][k];
        }

        const match = k.match(/\d+/g);
        if(match && k.startsWith(`${errorKey}supportingFiles.${fileType}.`)){
          const j = Number(match[match.length-1]);
          if(j===dialogDeleteState.index){
            delete errs[step][k];
          }else if(j>dialogDeleteState.index){
            delete errs[step][k];
            errs[step][k.replace(/\d+(?!.*\d)/,j-1)]=props.errors[step][k];
          }
        }
      });
    }
    setErrors(errs);

    handleCloseDeleteDialog();
    setSnackbarDelete({open: true, severity: "success", message: t("requestForm.otherSupportingDeleted")});
  }

  let supportingFilesCount = React.useMemo(()=>{
    let count = 0;
    Object.keys(supportingFiles).forEach((k)=>{
      count+=supportingFiles[k].length;
    })
    return count;
  }, [supportingFiles]);

  let mandatorySpan = (fileType==="other" || !mandatorySection) ? <></> : <><span aria-hidden="true">*</span>
  <span className="screen-reader-text">{t("inputs.required")}</span></>;
  
  return <>
    {props.tooltip === undefined ?
      <>
        <Typography 
          id={`${fileType}-header`}
          variant="body2" 
          component="p" 
          sx={{ mb: fileType === "other" ? 2 : 0}}
          className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
        >
          {t("requestForm.modifyFile.addSupportingFilesTitle", {context: fileType})+ (!extraParagraph?"... ":"")}
          {!extraParagraph && mandatorySpan}
          
        </Typography>
        {extraParagraph &&
          <Typography 
          id={`${fileType}-header-para2`}
          variant="body2" 
          component="p" 
          sx={{ mb: fileType === "other" ? 2 : 0}}
          className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
        >
          {t("requestForm.modifyFile.addSupportingFilesTitle", {context: fileType + "_para2"})+"... "}
          {mandatorySpan}
          
        </Typography>
        }
        {extraNote && 
        <Typography 
        id={`${fileType}-header-para2`}
        variant="body2" 
        component="p" 
        sx={{ mb: fileType === "other" ? 2 : 0}}
        className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
        >
        {t("requestForm.modifyFile.addSupportingFilesTitle", {context: fileType + "_note"})} 
        
      </Typography>
        }

        {(fileType!=="other" && mandatorySection) &&
          <Typography 
            variant="caption"
            color="textSecondary"
            component="p"
            sx={{mb: 2}}
            className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
          >
            {t("requestForm.modifyFile.addSupportingFilesOneFile")}
          </Typography>
        }
      </>
      :
      <>
        <Grid container sx={{mt: 3}}>
          <Grid xs item>
          <Typography 
            id={`${fileType}-header`}
            variant="body2" 
            component="p" 
            sx={{ mb: fileType === "other" ? 2 : 0}}
            className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
          >
            {t("requestForm.modifyFile.addSupportingFilesTitle", {context: fileType})+"... "}
            {mandatorySpan}
          </Typography>
          {fileType!=="other" &&
            <Typography 
              variant="caption"
              color="textSecondary"
              component="p"
              sx={{mb: 2}}
              className={props.errors[step]?.[`${errorKey}supportingFiles.${fileType}`] ? classes.errorText : ''}
            >
              {t("requestForm.modifyFile.addSupportingFilesOneFile")}
            </Typography>
          }
          </Grid>
          <Grid className={classes.legendInfoIcon} item>
            <Tooltip title={props.tooltip} arrow>
              <IconButton
                aria-label={t('requestForm.toolTipMoreInformation')}
                edge="end"
                size="large">
                <Icon path={mdiInformationOutline} size={1} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </>
    }
    <Paper className="paper-grey" sx={{mb:3}} variant="outlined">
      {supportingFiles[fileType].length > 0 ?
      (supportingFiles[fileType].map((file, i)=>{
        return (
          <Card
            key={`${fileType}-${i}`}
            title={t("requestForm.modifyFile.addSupportingFilesCardTitle", {index: i+1, fileType: props.fileTypeDisplay ? props.fileTypeDisplay : fileType, context: "noExplain"})}
            titleComponent="h4"
            error={t(errors[step]?.[`${errorKey}supportingFiles.${fileType}.${i}.fileName`])}
            primaryButton={t("requestForm.modifyFile.addSupportingFilesEdit")}
            secondaryButton={t("requestForm.modifyFile.addSupportingFilesDelete")}
            primaryClick={()=>handleOpenEditFile(i)}
            secondaryClick={()=>handleOpenDeleteDialog(i)}
            readOnly={readOnly}
            isOutput={false}
            name={`${pre_name}supportingFiles-${fileType}-${i}`}
            content={
              <>
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  {t("requestForm.modifyFile.addSupportingFilesFilePath")}
                </Typography>
                <Box className={classes.filePath} sx={{mb: 2}}>
                  <div className={classes.filePathItem}>
                    <Typography 
                      id={`${pre_name}supportingFiles-${fileType}-${i}-fileName`} 
                      variant="body2" 
                      component="p"
                    >
                      {file.fileName}
                    </Typography>
                  </div>
                </Box>
                {file.notes !== "" &&
                  <>
                    <Typography
                      variant="caption"
                      component="p"
                      color="textSecondary"
                    >
                      {t("requestForm.modifyFile.addSupportingFilesNotes")}
                    </Typography>
                    <Typography 
                      id={`${pre_name}supportingFiles-${fileType}-${i}-notes`} 
                      variant="body2" 
                      component="p"
                    >
                      {file.notes}
                    </Typography>
                  </>
                }
              </>
            }
          />
        )})
      ) : (
      <Typography variant="caption" component="p" color="textSecondary">
        {t("requestForm.modifyFile.addSupportingFilesNoSupport")}
      </Typography>
      )}
    </Paper>
    {!readOnly &&
      <>
        {supportingFilesCount < maxFileLimit ?
          <Button
            aria-describedby={`${fileType}-header`}
            id={`${pre_name}supportingFiles-${fileType}-addBtn`}
            variant="outlined"
            color="primary"
            startIcon={<Icon path={mdiPlus} size={1} />}
            fullWidth
            className={addBtnClass}
            onClick={handleOpenAddFile}
          >
            {t("requestForm.modifyFile.addSupportingFilesButton")}
          </Button>
          :
          <Tooltip
            title={t("requestForm.filesList.maxSupportFiles")}
            arrow
          >
            <span>
              <Button
                id={`${pre_name}supportingFiles-${fileType}-addBtn`}
                variant="outlined"
                color="primary"
                startIcon={<Icon path={mdiPlus} size={1} />}
                fullWidth
                className={addBtnClass}
                disabled={true}
              >
                {t("requestForm.modifyFile.addSupportingFilesButton")}
              </Button>
            </span>
          </Tooltip>
        }
      </>
    }
    {!readOnly &&
      <DialogFileSupport 
        submitDialog={alertAddFile.index === null ? handleAddNewSupporting : handleEditNewSupporting}
        toggleDialog={handleCloseAddSupporting}
        handleOnChange={(name, newValue)=>handleAddSupportingValueChange(name, newValue)}
        open={alertAddFile.open}
        filesList={filesList}
        newSupporting={alertAddFile.newSupporting}
        index={alertAddFile.index}
        fileType={fileType}
        snackbar={
          <Snackbar 
            message={snackbarFile.message}
            severity={snackbarFile.severity}
            open={snackbarFile.open}
            handleClose={handleCloseSnackbarFile}
          />
        }
      />
    }
    <DialogDelete 
      toggleDialog={handleCloseDeleteDialog}
      handleDelete={handleDeleteSupportingFile} 
      open={dialogDeleteState.dialogDelete} 
      snackbar={
        <Snackbar 
          message={snackbarDelete.message}
          severity={snackbarDelete.severity}
          open={snackbarDelete.open}
          handleClose={handleCloseSnackbarDelete}
        />
      }
    />
  </>;
}
AddSupportingFiles.propTypes={
  step: PropTypes.string.isRequired,
  outputIndex: PropTypes.number,
  filesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  supportingFiles: PropTypes.object.isRequired,
  fileType: PropTypes.string.isRequired,
  fileTypeDisplay: PropTypes.string,
  readOnly: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  handleUpdateSupportingFiles: PropTypes.func.isRequired,
  addBtnClass: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  mandatorySection: PropTypes.bool.isRequired,
  extraParagraph: PropTypes.bool.isRequired,
  extraNote: PropTypes.bool,
}